import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, Plus, Printer, QrCode, Trash2 } from "lucide-react";

const EMPTY = { code: "", doctor_name: "", location: "" };

/**
 * Admin → Referral sources: one code per referring doctor or clinic, its QR poster
 * in PNG / SVG / print-ready A5, and how many scans and uploads it has produced.
 */
export const ReferralSourcesTab = () => {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/referral-sources").then(({ data }) => setSources(data.sources || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/admin/referral-sources", { ...form, active: true });
      toast.success(`${form.code.toUpperCase()} added`);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/admin/referral-sources/${row.code}`, { active: !row.active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (code) => {
    try {
      await api.delete(`/admin/referral-sources/${code}`);
      toast.success(`${code} removed`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // The QR endpoints sit behind the admin login, so the bytes are fetched with the
  // session and handed to the browser as a download.
  const download = async (code, kind) => {
    const path = kind === "sheet" ? "sheet.pdf" : `qr.${kind}`;
    try {
      const { data } = await api.get(`/admin/referral-sources/${code}/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "sheet" ? `referral-${code}-a5.pdf` : `qr-${code}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // One print run for the whole clinic: an index page, then an A5 poster per doctor.
  const downloadBatch = async (include) => {
    try {
      const { data } = await api.get("/admin/referral-sources/posters.pdf", {
        params: { include }, responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `referral-posters-${include}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Poster pack downloaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No posters to print yet"); }
  };

  return (
    <Card className="border-border" data-testid="referral-sources-tab">
      <CardContent className="p-6 lg:p-8">
        <h3 className="font-display text-2xl flex items-center gap-2 mb-1"><QrCode size={18} /> Referral sources</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Give every referring doctor their own code and poster. Each scan, upload and paid order is
          counted against it.
        </p>

        <div className="flex flex-wrap gap-2 mb-6">
          <Button variant="outline" className="btn-pill h-9" onClick={() => downloadBatch("active")}
                  data-testid="ref-batch-active">
            <Printer size={13} className="mr-1.5" /> Print all active posters
          </Button>
          <Button variant="ghost" className="btn-pill h-9" onClick={() => downloadBatch("all")}
                  data-testid="ref-batch-all">
            Include paused ones
          </Button>
        </div>

        <div className="grid sm:grid-cols-4 gap-3 items-end mb-8 pb-6 border-b border-border">
          <div>
            <label className="overline">Code</label>
            <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                   placeholder="DR001" className="mt-2 bg-transparent" data-testid="ref-code" />
          </div>
          <div>
            <label className="overline">Doctor name</label>
            <Input value={form.doctor_name} onChange={(e) => setForm((f) => ({ ...f, doctor_name: e.target.value }))}
                   placeholder="Dr. Nikhil Shah" className="mt-2 bg-transparent" data-testid="ref-doctor" />
          </div>
          <div>
            <label className="overline">Clinic / location</label>
            <Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                   placeholder="Shree Clinic, Ahmedabad" className="mt-2 bg-transparent" data-testid="ref-location" />
          </div>
          <Button onClick={add} disabled={busy || !form.code.trim()} className="btn-action h-11" data-testid="ref-add">
            <Plus size={15} className="mr-1.5" /> Add source
          </Button>
        </div>

        {sources.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="ref-empty">
            No referral sources yet — add the first doctor above.
          </p>
        ) : (
          <div className="space-y-3">
            {sources.map((row) => (
              <div key={row.code} className="rounded-xl border border-border p-4" data-testid={`ref-row-${row.code}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant="outline" className="font-mono">{row.code}</Badge>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{row.doctor_name || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate">{row.location || row.url}</div>
                  </div>
                  <div className="ml-auto flex items-center gap-2 text-xs">
                    <Badge variant="outline" data-testid={`ref-visits-${row.code}`}>{row.visits} visits</Badge>
                    <Badge variant="outline" data-testid={`ref-uploads-${row.code}`}>{row.uploads} uploads</Badge>
                    <Badge variant="outline" data-testid={`ref-orders-${row.code}`}>
                      {row.orders || 0} paid · ₹{Number(row.revenue || 0).toFixed(0)}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={row.active ? "border-primary/40 text-primary bg-primary/10" : "border-border"}
                    >
                      {row.active ? "active" : "paused"}
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" variant="outline" className="btn-pill h-8" onClick={() => download(row.code, "png")}
                          data-testid={`ref-png-${row.code}`}>
                    <Download size={12} className="mr-1.5" /> QR PNG
                  </Button>
                  <Button size="sm" variant="outline" className="btn-pill h-8" onClick={() => download(row.code, "svg")}
                          data-testid={`ref-svg-${row.code}`}>
                    <Download size={12} className="mr-1.5" /> QR SVG
                  </Button>
                  <Button size="sm" variant="outline" className="btn-pill h-8" onClick={() => download(row.code, "sheet")}
                          data-testid={`ref-sheet-${row.code}`}>
                    <FileText size={12} className="mr-1.5" /> A5 poster
                  </Button>
                  <Button size="sm" variant="ghost" className="btn-pill h-8" onClick={() => toggle(row)}
                          data-testid={`ref-toggle-${row.code}`}>
                    {row.active ? "Pause" : "Activate"}
                  </Button>
                  <Button size="sm" variant="ghost" className="btn-pill h-8 text-destructive"
                          onClick={() => remove(row.code)} data-testid={`ref-delete-${row.code}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferralSourcesTab;
